// Shared attribution UI for V2 task rows: who completed / paused / rescheduled a
// task, with avatar + name + time (and a distinct "System" indicator for
// automated actions). Reused by LeadTaskList's TaskRow and TaskChecklistCard's
// TemplateGroup so the grouped Tasks tab renders attribution consistently.
import PropTypes from 'prop-types';
import { Avatar, Box, Chip, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { resolveCompleter, resolvePauser } from 'src/utils/taskAttribution';
import { formatTaskDate } from 'src/utils/taskDateUtils';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif';
const MUTED = '#86868B';
const SUCCESS = '#34C759';
const WARNING = '#FF9500';

// Muted circular system indicator (gear/robot) used wherever an automated,
// no-user action is attributed.
function SystemAvatar({ size }) {
    return (
        <Avatar sx={{ width: size, height: size, bgcolor: 'rgba(0,0,0,0.06)', color: MUTED }}>
            <Iconify icon="mdi:robot-outline" width={size * 0.7} />
        </Avatar>
    );
}
SystemAvatar.propTypes = { size: PropTypes.number };
SystemAvatar.defaultProps = { size: 16 };

// Initials-or-photo avatar for a real user (photo if present, else initials).
function PersonAvatar({ name, src, size }) {
    return (
        <Avatar src={src || undefined} sx={{ width: size, height: size, fontSize: size * 0.5, bgcolor: 'rgba(0, 122, 255, 0.10)', color: '#007AFF' }}>
            {(name || '?').charAt(0).toUpperCase()}
        </Avatar>
    );
}
PersonAvatar.propTypes = { name: PropTypes.string, src: PropTypes.string, size: PropTypes.number };
PersonAvatar.defaultProps = { name: '', src: undefined, size: 16 };

/**
 * Completer attribution: `[avatar] Name · 18 Jun` or `[gear] System · 18 Jun`.
 * Returns null when there's nothing to attribute (open task / no date).
 */
export function CompleterAttribution({ task, dateLabel, color, size }) {
    const { name, avatarUrl, isSystem } = resolveCompleter(task);
    if (!name || !dateLabel) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {isSystem ? <SystemAvatar size={size} /> : <PersonAvatar name={name} src={avatarUrl} size={size} />}
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 400, color, whiteSpace: 'nowrap' }}>
                {isSystem ? 'System' : name} · {dateLabel}
            </Typography>
        </Box>
    );
}
CompleterAttribution.propTypes = {
    task: PropTypes.object.isRequired,
    dateLabel: PropTypes.string,
    color: PropTypes.string,
    size: PropTypes.number,
};
CompleterAttribution.defaultProps = { dateLabel: null, color: SUCCESS, size: 16 };

/**
 * Paused attribution line: `[avatar] Paused · resumes {date} · by {who}` (or
 * "by System"). `pauseSource` is the paused checklist item (or the task) used
 * to resolve who paused it.
 */
export function PausedAttribution({ pauseSource, resumeLabel, size }) {
    const { name, avatarUrl, isSystem } = resolvePauser(pauseSource || {});
    const whoLabel = isSystem ? 'System' : name;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {whoLabel && (isSystem ? <SystemAvatar size={size} /> : <PersonAvatar name={whoLabel} src={avatarUrl} size={size} />)}
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 400, color: WARNING, whiteSpace: 'nowrap' }}>
                Paused{resumeLabel ? ` · resumes ${resumeLabel}` : ''}{whoLabel ? ` · by ${whoLabel}` : ''}
            </Typography>
        </Box>
    );
}
PausedAttribution.propTypes = {
    pauseSource: PropTypes.object,
    resumeLabel: PropTypes.string,
    size: PropTypes.number,
};
PausedAttribution.defaultProps = { pauseSource: null, resumeLabel: null, size: 16 };

// Reschedule audit fields are NOT currently exposed on the task / checklist-item
// payloads (no rescheduled_at / rescheduled_by / original_due_at — verified by
// grep against the FE + the /tasks/ and /task-checklists/ response usage). The
// only reschedule signal is the `task_due_date_changed` verb in the lead
// activities feed, which we don't join here. So the chip renders ONLY when an
// explicit reschedule field appears on the task (forward-compatible — it lights
// up automatically once BE adds it). See report for the BE-field ask.
export function getRescheduleInfo(task) {
    if (!task || typeof task !== 'object') return null;
    const at = task.rescheduled_at || task.due_at_changed_at || null;
    const by = task.rescheduled_by || task.due_at_changed_by || null;
    const from = task.original_due_at || task.previous_due_at || null;
    const count = task.reschedule_count || 0;
    const wasRescheduled = Boolean(at) || count > 0 || Boolean(from);
    if (!wasRescheduled) return null;
    let byName = '';
    if (typeof by === 'string') byName = by;
    else if (by && typeof by === 'object') byName = by.full_name || `${by.first_name || ''} ${by.last_name || ''}`.trim() || by.email || '';
    return { at, byName, from };
}

/**
 * "Rescheduled" chip with a hover tooltip showing who/when (when available).
 * Renders nothing when there's no reschedule signal on the task.
 */
export function RescheduledChip({ task }) {
    const info = getRescheduleInfo(task);
    if (!info) return null;
    const whenLabel = formatTaskDate(info.at);
    const fromLabel = formatTaskDate(info.from);
    let tip = 'Rescheduled';
    if (info.byName) tip += ` by ${info.byName}`;
    if (whenLabel) tip += ` · ${whenLabel}`;
    if (fromLabel) tip += ` (was ${fromLabel})`;
    return (
        <Tooltip title={tip} placement="top" arrow>
            <Chip
                label="Rescheduled"
                size="small"
                sx={{
                    height: 18,
                    fontFamily: FONT,
                    fontSize: '10px',
                    fontWeight: 500,
                    bgcolor: 'rgba(255, 149, 0, 0.12)',
                    color: '#B76E00',
                    borderRadius: '5px',
                    flexShrink: 0,
                    '& .MuiChip-label': { px: '6px' },
                }}
            />
        </Tooltip>
    );
}
RescheduledChip.propTypes = { task: PropTypes.object.isRequired };
